import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';

import { getApiErrorMessage } from '@core/http/api-error-message.util';
import { NotificationService } from '@shared/services/notification.service';
import {
  ActivityConfigurationDto,
  ActivityManagementService,
  UpsertActivityConfigurationDto,
} from '../../services/activity-management.service';
import { AreaDto, AreaManagementService } from '../../services/area-management.service';

interface OrganizationLevelOption {
  id: number;
  name: string;
}

@Component({
  selector: 'app-activity-editor',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatTooltipModule,
  ],
  templateUrl: './activity-editor.component.html',
  styleUrl: './activity-editor.component.scss',
})
export class ActivityEditorComponent implements OnInit, OnDestroy {
  areaOptions: AreaDto[] = [];
  readonly organizationLevels: OrganizationLevelOption[] = [
    { id: 1, name: 'Iglesia Regional' },
    { id: 2, name: 'Área Apóstol' },
    { id: 3, name: 'Distrito' },
    { id: 4, name: 'Grupo Comunidad' },
    { id: 5, name: 'Comunidad' },
  ];

  form: FormGroup;
  isLoading = false;
  isSaving = false;
  isEditMode = false;
  activityId: number | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly service: ActivityManagementService,
    private readonly areaService: AreaManagementService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly notificationService: NotificationService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(150)]],
      description: ['', [Validators.maxLength(500)]],
      areaId: [null as number | null, [Validators.required]],
      allowedOrganizationLevelIds: [[5] as number[], [Validators.required, Validators.minLength(1)]],
      isActive: [true],
      roles: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    this.loadAreas();

    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params['id'] && params['id'] !== 'new') {
          this.activityId = +params['id'];
          this.isEditMode = true;
          this.loadActivity();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get roles(): FormArray {
    return this.form.get('roles') as FormArray;
  }

  get pageTitle(): string {
    return this.isEditMode ? 'Editar colaboración' : 'Nueva colaboración';
  }

  addRole(): void {
    this.roles.push(this.createRoleGroup());
    this.cdr.markForCheck();
  }

  removeRole(index: number): void {
    this.roles.removeAt(index);
    this.normalizeDefaultRole();
    this.cdr.markForCheck();
  }

  setRoleDefault(index: number, isDefault: boolean): void {
    this.roles.controls.forEach((role, roleIndex) => role.get('isDefault')?.setValue(isDefault && roleIndex === index, { emitEvent: false }));
    this.cdr.markForCheck();
  }

  setRoleActive(index: number, isActive: boolean): void {
    this.roles.at(index).get('isActive')?.setValue(isActive);
    if (!isActive) this.roles.at(index).get('isDefault')?.setValue(false);
    this.cdr.markForCheck();
  }

  saveActivity(): void {
    if (!this.form.valid) {
      this.notificationService.warning('Por favor, completa todos los campos requeridos');
      return;
    }

    this.isSaving = true;
    this.cdr.markForCheck();

    const dto = this.toDto();

    if (this.isEditMode && this.activityId) {
      this.service.update(this.activityId, dto)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.notificationService.success('Colaboración actualizada exitosamente');
            this.router.navigate(['/configuration/activities']);
          },
          error: error => this.handleSaveError(
            error,
            'No se pudo actualizar la colaboración. Revisá los datos cargados e intentá nuevamente'
          ),
        });
      return;
    }

    this.service.create(dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.success('Colaboración creada exitosamente');
          this.router.navigate(['/configuration/activities']);
        },
        error: error => this.handleSaveError(
          error,
          'No se pudo crear la colaboración. Revisá los datos cargados e intentá nuevamente'
        ),
      });
  }

  cancel(): void {
    this.router.navigate(['/configuration/activities']);
  }

  getControl(name: string) {
    return this.form.get(name);
  }

  private loadActivity(): void {
    if (!this.activityId) {
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    this.service.getById(this.activityId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: activity => {
          this.patchForm(activity);
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.notificationService.error('No se pudo cargar la colaboración seleccionada');
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private patchForm(activity: ActivityConfigurationDto): void {
    this.form.patchValue({
      name: activity.name,
      description: activity.description ?? '',
      areaId: activity.areaId,
      allowedOrganizationLevelIds: activity.allowedOrganizationLevelIds,
      isActive: activity.isActive,
    });

    this.roles.clear();
    activity.roles.forEach(role => {
      this.roles.push(this.createRoleGroup({
        id: role.id,
        name: role.name,
        shortName: role.shortName ?? '',
        description: role.description ?? '',
        isActive: role.isActive,
        isDefault: role.isDefault,
      }));
    });

  }

  private createRoleGroup(value?: any): FormGroup {
    return this.fb.group({
      id: [value?.id ?? null],
      name: [value?.name ?? '', [Validators.required, Validators.minLength(2), Validators.maxLength(150)]],
      shortName: [value?.shortName ?? '', [Validators.maxLength(50)]],
      description: [value?.description ?? '', [Validators.maxLength(500)]],
      isActive: [value?.isActive ?? true],
      isDefault: [value?.isDefault ?? false],
    });
  }

  private normalizeDefaultRole(): void {
    let foundDefault = false;
    this.roles.controls.forEach(role => {
      const isDefault = role.get('isDefault')?.value === true && role.get('isActive')?.value === true && !foundDefault;
      role.get('isDefault')?.setValue(isDefault, { emitEvent: false });
      foundDefault ||= isDefault;
    });
  }

  private toDto(): UpsertActivityConfigurationDto {
    const value = this.form.value;
    return {
      name: value.name,
      description: value.description || null,
      areaId: value.areaId,
      allowedOrganizationLevelIds: value.allowedOrganizationLevelIds,
      isActive: value.isActive,
      roles: value.roles.map((role: any) => ({
        id: role.id,
        name: role.name,
        shortName: role.shortName || null,
        description: role.description || null,
        isActive: role.isActive,
        isDefault: role.isDefault,
      })),
    };
  }

  private handleSaveError(error: any, fallback: string): void {
    this.notificationService.error(getApiErrorMessage(error, fallback));
    this.isSaving = false;
    this.cdr.markForCheck();
  }

  private loadAreas(): void {
    this.areaService.getActive()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: areas => {
          this.areaOptions = areas;
          if (!this.isEditMode && areas.length > 0 && !this.form.get('areaId')?.value) {
            this.form.patchValue({ areaId: areas[0].id });
          }
          this.cdr.markForCheck();
        },
        error: () => this.notificationService.error('No se pudieron cargar las áreas'),
      });
  }
}
